import { useRef, useState } from "react";
import type {
  EpisodeRef,
  NavItem,
  OutputLine,
  PatchOutputInput,
  ProjectDetail,
  StoryboardView,
} from "@dramapie/shared";

import type { AccountSummary } from "../../lib/account";
import { initialOf } from "../../lib/account";
import { NavLinks } from "../NavLinks";
import {
  LIBRARY_TAB_LABELS,
  LIBRARY_TAB_ORDER,
  type LibraryTab,
} from "../../lib/library-tabs";
import {
  countEpisodesByOutput,
  episodesOfOutput,
  findOutput,
  outputMetaLabel,
} from "../../lib/outputs";
import { episodeCode, STYLE_LABEL } from "../../lib/view";
import {
  CONTENT_NAV,
  isContentView,
  type WorkspaceContentView,
  type WorkspaceTab,
} from "../../lib/workspace-tabs";
import { EpisodeMoveMenu } from "./EpisodeMoveMenu";
import { OutputMenu } from "./OutputMenu";

export interface RailProps {
  project: ProjectDetail | null;
  view: StoryboardView | null;
  activeEpisodeId: string | undefined;
  onSelectEpisode: (episodeId: string) => void;
  /** 当前主区视图：左栏内容导航据此高亮（投影视图时内容项全部不亮）。 */
  viewMode: WorkspaceTab;
  /** 内容导航：切主区视图（资料库 / 大纲 / 剧本 / 数据），不是弹层。 */
  onOpenContent: (content: WorkspaceContentView) => void;
  /** 资料库二级导航：世界 / 角色 / 道具 / 术语 / 资产版本。 */
  libraryTab: LibraryTab;
  onSelectLibraryTab: (tab: LibraryTab) => void;
  /**
   * M8 产出线：全量（含 hidden / archived，浮层里要能恢复）。
   * 左栏**只占一行**显示当前产出线，点开才是浮层（原型 renderRail）。
   */
  outputs: readonly OutputLine[];
  currentOutputId: string | null;
  /** 全量还没回来时的标题兜底（来自项目详情）。 */
  currentOutputTitle?: string | null;
  onSelectOutput: (outputId: string) => void;
  onCreateOutput: () => void;
  onRenameOutput: (output: OutputLine) => void;
  onPatchOutput: (output: OutputLine, patch: PatchOutputInput) => void;
  onDeleteOutput: (output: OutputLine) => void;
  /** 把某一集移到别的产出线（M8 补充）。 */
  onMoveEpisode: (episodeId: string, outputId: string) => void;
  /** 产出线写操作进行中：禁用浮层里的按钮，避免重复提交。 */
  outputBusy?: boolean;
  /** 左下角登录用户（Wiki G-05）：点开用户设置浮层。 */
  account: AccountSummary | null;
  accountLoading?: boolean;
  /** 用户设置浮层是否打开（给 `aria-expanded`）。 */
  accountOpen?: boolean;
  onOpenAccount: () => void;
  loading?: boolean;
  /** 作品切换（Wiki G-01）：点品牌区打开浮层。 */
  switcherOpen?: boolean;
  onToggleSwitcher?: () => void;
  /** 浮层内容由页面渲染（页面管数据与关闭逻辑）。 */
  switcherSlot?: React.ReactNode;
  /** 跨站导航（P1-W3/W4）：不传由底部 NavLinks 自己拉 `/api/nav`。 */
  navItems?: readonly NavItem[];
  /** 当前站点 host（测试注入）；缺省 `window.location.host`。 */
  currentHost?: string;
}

/** 内容导航图标：16px 线性图标，颜色跟着当前项走（不引第三方图标库）。 */
function ContentNavIcon({ id }: { id: WorkspaceContentView }) {
  const common = {
    width: 15,
    height: 15,
    viewBox: "0 0 16 16",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.3,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
  };
  if (id === "library") {
    return (
      <svg {...common}>
        <path d="M3 2.5h6.2L13 6.3V13.5H3z" />
        <path d="M9 2.5v4h4" />
        <path d="M5.4 9h5.2M5.4 11.2h3.4" />
      </svg>
    );
  }
  if (id === "outline") {
    return (
      <svg {...common}>
        <path d="M3 3.2h10M3 8h10M3 12.8h10" />
        <circle cx="1.4" cy="3.2" r="0.7" fill="currentColor" stroke="none" />
        <circle cx="1.4" cy="8" r="0.7" fill="currentColor" stroke="none" />
        <circle cx="1.4" cy="12.8" r="0.7" fill="currentColor" stroke="none" />
      </svg>
    );
  }
  if (id === "script") {
    return (
      <svg {...common}>
        <path d="M3.2 2.6h6.4l3.2 3.2v7.6H3.2z" />
        <path d="M5.5 7.2h5M5.5 9.4h5M5.5 11.6h3.2" />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <path d="M5.6 2.8C4 2.8 3 3.8 3 5.3 3 6.6 3.6 7 3.6 8.2 3.6 9.5 3 9.7 3 11c0 1.5 1 2.4 2.6 2.4" />
      <path d="M10.4 2.8c1.6 0 2.6 1 2.6 2.5 0 1.3-.6 1.7-.6 2.9 0 1.3.6 1.5.6 2.8 0 1.5-1 2.4-2.6 2.4" />
      <path d="M6.6 6.4h2.8M6.6 9.8h2.8" />
    </svg>
  );
}

/**
 * 左轨（236px）：品牌 / 产出线 / 集列表 / **内容导航** / 资料库统计 / 跨站导航 / 登录用户。
 *
 * 内容导航（资料库 / 大纲 / 剧本 / 数据）切的是主区视图，不是弹层；
 * 资料库下面再列一级分类，点了直接进资料库视图的对应 tab。
 *
 * 只展示真实数据：集状态点需要镜头状态，而 `EpisodeRef` 没有，
 * 所以非当前集用中性点，不按集编造状态。
 *
 * 底部固定块按原型改成「当前登录用户 + 齿轮」（G-05）——
 * 原型这里放的是成本，但用户明确要用户信息；成本/用量改从用户设置里进。
 * 用户信息**正上方**是跨站导航（工作室 · 画布 · 账户，P1-W3/W4 第 1 条）：
 * 平铺一行，不再占用品牌区顶部的下拉。
 */
export function Rail({
  project,
  view,
  activeEpisodeId,
  onSelectEpisode,
  viewMode,
  onOpenContent,
  libraryTab,
  onSelectLibraryTab,
  outputs,
  currentOutputId,
  currentOutputTitle,
  onSelectOutput,
  onCreateOutput,
  onRenameOutput,
  onPatchOutput,
  onDeleteOutput,
  onMoveEpisode,
  outputBusy = false,
  account,
  accountLoading = false,
  accountOpen = false,
  onOpenAccount,
  loading = false,
  switcherOpen = false,
  onToggleSwitcher,
  switcherSlot,
  navItems,
  currentHost,
}: RailProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  /** 正在移动的那一集（浮层贴在这一行下面）。 */
  const [moveTarget, setMoveTarget] = useState<{
    episodeId: string;
    label: string;
    anchor: HTMLElement;
  } | null>(null);

  /**
   * 项目详情慢 / 失败时，/view 里已经有当前集的真实信息：
   * 至少把当前集显示出来，左轨不会因为一个接口卡住而整块空白。
   */
  const allEpisodes: EpisodeRef[] =
    project?.episodes ??
    (view
      ? [
          {
            id: view.episode.id,
            no: view.episode.no,
            title: view.episode.title,
            sceneCount: view.scenes.length,
            shotCount: view.shots.length,
            // 兜底路径拿不到归属，直接用当前产出线（左栏本来就是按它在过滤）。
            outputId: currentOutputId ?? "out_short",
          },
        ]
      : []);

  // 契约 §3：切换产出线 = 切换集列表。隐藏 / 归档的线仍然有集，只是不在主列表里。
  const episodes = episodesOfOutput(allEpisodes, currentOutputId);
  const episodeCounts = countEpisodesByOutput(allEpisodes);
  const currentOutput = findOutput(outputs, currentOutputId);
  const title = project?.title ?? "";
  const mark = title ? title.slice(0, 1) : "·";
  const styleLabel = project ? STYLE_LABEL[project.style] : "";

  const sceneCount = view?.scenes.length ?? 0;
  const shotCount = view?.shots.length ?? 0;
  const charCount = new Set((view?.shots ?? []).flatMap((shot) => shot.chars)).size;
  /** 投影视图（故事板 / 画布 / 时间线）时内容导航不高亮。 */
  const activeContent = isContentView(viewMode) ? viewMode : null;

  /**
   * 老项目兜底：一条产出线都没有时，左栏显示一条**只读**的「短剧（默认）」，
   * 不要空着（后端对老项目会返回默认骨架；这是接口失败/脏数据时的最后一道保险）。
   */
  const readOnlyFallback = outputs.length === 0 && !loading;
  const outputTitleText =
    currentOutput?.title ??
    currentOutputTitle ??
    (readOnlyFallback ? "短剧（默认）" : loading ? "读取中…" : "暂无产出线");
  /** 移集浮层的候选：别的、未归档的线。 */
  const moveTargets = outputs.filter(
    (output) => output.id !== currentOutputId && !output.archived,
  );

  const userName = account?.name ?? "";
  const initial = initialOf(userName);

  return (
    <aside className="dp-rail" aria-label="项目导航">
      <div className="dp-brand">
        {/* 作品切换 —— 保持原来的 dp-brand-btn / chevron / h1 行为不变。 */}
        <button
          type="button"
          className="dp-brand-btn"
          aria-haspopup="menu"
          aria-expanded={switcherOpen}
          title="切换作品"
          onClick={onToggleSwitcher}
        >
        <span className="dp-brand-mark" aria-hidden="true">
          {mark}
        </span>
        <div className="dp-brand-text">
          {loading && !project ? (
            <div className="dp-skel-block" style={{ width: 88, height: 16 }} aria-hidden="true" />
          ) : (
            <h1 className="dp-brand-name">{title || "未命名项目"}</h1>
          )}
          <p className="dp-brand-sub">
            {project
              ? `${styleLabel} · 共 ${allEpisodes.length} 集`
              : loading
                ? "读取中…"
                : "暂无项目数据"}
          </p>
        </div>
        <span className="dp-brand-chevron" aria-hidden="true">
          ⌄
        </span>
        </button>
        {switcherSlot}
      </div>

      {/* 产出线只占一行（原型 renderRail）：当前线 + 集数，点开才是浮层。 */}
      <section className="dp-rail-section dp-output-section" aria-labelledby="rail-outputs">
        <h2 className="dp-rail-label" id="rail-outputs">
          <span>产出线</span>
          <span aria-hidden="true">
            {outputs.filter((output) => !output.hidden && !output.archived).length} 条
          </span>
        </h2>
        <div className="dp-output-trigger-row">
          <button
            ref={triggerRef}
            type="button"
            className={`dp-output-trigger${menuOpen ? " is-open" : ""}`}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-label={
              readOnlyFallback
                ? "产出线：短剧（默认），只读；点右上＋新建"
                : `产出线：${outputTitleText}，点开切换或管理`
            }
            disabled={readOnlyFallback}
            onClick={() => setMenuOpen((value) => !value)}
          >
            <span className="dp-output-mark" aria-hidden="true">
              {currentOutput ? currentOutput.title.slice(0, 1) : "?"}
            </span>
            <span className="dp-ot-copy">
              <span className="dp-ot-name">
                {outputTitleText}
                {currentOutput?.archived ? <i>已归档</i> : null}
                {currentOutput?.hidden && !currentOutput.archived ? <i>已隐藏</i> : null}
              </span>
              <span className="dp-ot-meta">
                {currentOutput
                  ? outputMetaLabel(currentOutput, episodeCounts[currentOutput.id] ?? 0)
                  : readOnlyFallback
                    ? "默认产出线 · 只读"
                    : `${episodeCounts[currentOutputId ?? ""] ?? 0} 集`}
              </span>
            </span>
            <span className="dp-ot-chev" aria-hidden="true">
              ⌄
            </span>
          </button>
          <button
            type="button"
            className="dp-output-plus"
            aria-label="新建产出线"
            title="新建产出线"
            disabled={outputBusy}
            onClick={onCreateOutput}
          >
            ＋
          </button>
        </div>

        <OutputMenu
          open={menuOpen}
          outputs={outputs}
          currentOutputId={currentOutputId}
          episodeCounts={episodeCounts}
          anchor={triggerRef.current}
          busy={outputBusy}
          onClose={() => setMenuOpen(false)}
          onSelect={(outputId) => {
            setMenuOpen(false);
            onSelectOutput(outputId);
          }}
          onCreate={() => {
            setMenuOpen(false);
            onCreateOutput();
          }}
          onRename={(output) => {
            setMenuOpen(false);
            onRenameOutput(output);
          }}
          onPatch={(output, patch) => onPatchOutput(output, patch)}
          onDelete={(output) => onDeleteOutput(output)}
        />
      </section>

      <EpisodeMoveMenu
        open={moveTarget !== null}
        anchor={moveTarget?.anchor ?? null}
        episodeLabel={moveTarget?.label ?? ""}
        targets={moveTargets}
        busy={outputBusy}
        onClose={() => setMoveTarget(null)}
        onSelect={(outputId) => {
          const episodeId = moveTarget?.episodeId;
          setMoveTarget(null);
          if (episodeId) onMoveEpisode(episodeId, outputId);
        }}
      />

      <section className="dp-rail-section" aria-labelledby="rail-episodes">
        <h2 className="dp-rail-label" id="rail-episodes">
          集 <span aria-hidden="true">{episodes.length}</span>
        </h2>
        {episodes.length === 0 ? (
          <p className="dp-tiny-note" style={{ padding: "0 6px" }}>
            {loading ? "读取中…" : currentOutput ? "该产出线还没有集" : "暂无剧集"}
          </p>
        ) : (
          <div className="dp-ep-list">
            {episodes.map((episode) => {
              const active = episode.id === activeEpisodeId;
              const moving = moveTarget?.episodeId === episode.id;
              return (
                <div className="dp-ep-row" key={episode.id}>
                  <button
                    type="button"
                    className="dp-ep-btn"
                    aria-current={active ? "page" : undefined}
                    aria-label={`第 ${episode.no} 集 · ${episode.title}`}
                    onClick={() => onSelectEpisode(episode.id)}
                  >
                    <span className="dp-ep-dot" aria-hidden="true" />
                    <span className="dp-ep-name">
                      第{episodeCode(episode.no)}话 · {episode.title}
                    </span>
                    {active ? <span className="dp-ep-now">当前</span> : null}
                  </button>
                  <button
                    type="button"
                    className="dp-ep-move"
                    aria-label={`移动第 ${episode.no} 集`}
                    aria-haspopup="menu"
                    aria-expanded={moving}
                    title="移到别的产出线"
                    disabled={outputBusy || moveTargets.length === 0}
                    onClick={(event) => {
                      const anchor = event.currentTarget;
                      setMoveTarget((prev) =>
                        prev?.episodeId === episode.id
                          ? null
                          : {
                              episodeId: episode.id,
                              label: `第 ${episode.no} 集 · ${episode.title}`,
                              anchor,
                            },
                      );
                    }}
                  >
                    ⇄
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* 内容导航：资料库 / 大纲 / 剧本 / 数据 —— 主区切换，不是弹层。 */}
      <section className="dp-rail-section dp-content-nav" aria-labelledby="rail-content">
        <h2 className="dp-rail-label" id="rail-content">
          <span>内容</span>
          <span aria-hidden="true">CONTENT</span>
        </h2>
        <nav aria-label="内容导航">
          {CONTENT_NAV.map((item) => {
            const active = activeContent === item.id;
            return (
              <div className="dp-content-nav-item" key={item.id}>
                <button
                  type="button"
                  className="dp-nav-item"
                  aria-current={active ? "page" : undefined}
                  aria-keyshortcuts={item.key}
                  title={item.title}
                  onClick={() => onOpenContent(item.id)}
                >
                  <span className="dp-nav-icon" aria-hidden="true">
                    <ContentNavIcon id={item.id} />
                  </span>
                  <span className="dp-nav-label">{item.label}</span>
                  <span className="dp-nav-key" aria-hidden="true">
                    {item.key}
                  </span>
                </button>
                {item.id === "library" ? (
                  <div className="dp-nav-sub" role="group" aria-label="资料库分类">
                    {LIBRARY_TAB_ORDER.map((id) => (
                      <button
                        key={id}
                        type="button"
                        aria-pressed={active && libraryTab === id}
                        title={`资料库 · ${LIBRARY_TAB_LABELS[id]}`}
                        onClick={() => onSelectLibraryTab(id)}
                      >
                        {LIBRARY_TAB_LABELS[id]}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            );
          })}
        </nav>
      </section>

      <div className="dp-rail-spacer" />

      {view ? (
        <section className="dp-rail-section" aria-labelledby="rail-library">
          <h2 className="dp-rail-label" id="rail-library">
            <span>资料库</span>
            <button
              type="button"
              className="dp-rail-open"
              aria-label="打开资料库"
              title="在主区打开资料库（角色 / 场景 / 道具）"
              onClick={() => onOpenContent("library")}
            >
              打开 ›
            </button>
          </h2>
          <button
            type="button"
            className="dp-lib-row"
            onClick={() => onSelectLibraryTab("characters")}
          >
            角色 <b>{charCount}</b>
          </button>
          <button
            type="button"
            className="dp-lib-row"
            onClick={() => onSelectLibraryTab("world")}
          >
            场景 <b>{sceneCount}</b>
          </button>
          <div className="dp-lib-row">
            镜头 <b>{shotCount}</b>
          </div>
        </section>
      ) : null}

      {/* 跨站导航平铺一行：账户信息正上方，工作室 · 画布 · 账户。 */}
      <NavLinks items={navItems} currentHost={currentHost} />

      <button
        type="button"
        className="dp-user"
        onClick={onOpenAccount}
        aria-haspopup="dialog"
        aria-expanded={accountOpen}
        aria-label={userName ? `用户设置：${userName}` : "用户设置"}
        title={account ? `${userName} · ${account.email}` : "用户设置"}
      >
        <span className="dp-user-avatar" aria-hidden="true">
          {initial}
        </span>
        <span className="dp-user-copy">
          {accountLoading && !account ? (
            <span className="dp-skel-block" style={{ width: 76, height: 12 }} aria-hidden="true" />
          ) : (
            <b>{userName || "未登录"}</b>
          )}
          <span className="dp-user-email">{account?.email ?? (accountLoading ? "读取中…" : "—")}</span>
        </span>
        <span className="dp-user-gear" aria-hidden="true">
          ⚙
        </span>
      </button>
    </aside>
  );
}
